import * as msal from '@azure/msal-browser'; // ESM
import axios from 'axios';
import settings from 'settings';
import * as helpers from 'library/helpers';
import { ApiEndpoints } from 'api/urls';

const ignoreTokenForEndpoints = [];
const ignoreTenancyForEndpoints = [ApiEndpoints.whoami];

const getTenancies = tenancies => tenancies.map(t => ({
  tenancyId: t.tenancy.id,
  tenancyName: t.tenancy.name,
  description: t.tenancy.description,
  timezone: t.tenancy.default_timezone,
  adGroup: t.tenancy.active_directory_group,
  canViewOthersRoster: t.tenancy.roster_view_can_select_user || false,
  optimiserState: t.tenancy.optimiser_state,
  otPresets: t.tenancy.ot_presets
}));

/**
 * @description Get user tenancy permissions based on tenancy
 * @param {array} tenancyPermissions - list of tenancy permissions
 * @param {object} tenancy - tenancy object
 * @return {array} tenancy permissions
 */
const getCurrentTenancyPermissions = (tenancyPermissions = [], tenancy = {}) => {
  const tenancyId = tenancy ? tenancy.tenancyId : -1;
  const found = tenancyPermissions.find(item => item.tenancy_id === tenancyId);
  return (found ? found.permissions.map(item => item.name) : []);
};

class MsalService {
  constructor(config) {
    this.CONSTANTS = {
      CR_USER_DATA: 'CR_USER_DATA'
    };

    const existingHash = window.location.hash;
    let pathDefault = window.location.href;
    if (existingHash) {
      pathDefault = pathDefault.replace(existingHash, '');
    }
    const msalConfig = {
      auth: {
        clientId: config.clientId,
        authority: `https://login.microsoftonline.com/${config.tenant}`,
        redirectUri: config.redirectUri || pathDefault,
      },
      cache: {
        cacheLocation: 'localStorage'
      },
      system: {
        loggerOptions: {
          loggerCallback(loglevel, message) {
            console.log(message);
          },
          piiLoggingEnabled: false,
          logLevel: msal.LogLevel.Warning
        }
      }
    };
    this.msalUser = null;
    this.user = null;
    // ------ Create instance with given config ------
    this.authContext = new msal.PublicClientApplication(msalConfig);
  }

  static createUser(userData) {
    const user = userData || {};
    const { id } = userData;
    const email = user.email || '';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const tenancies = getTenancies(user.tenancies);
    const tenancyPermissions = user.tenancy_permissions || [];
    const currentTenancy = tenancies.length === 1 ? tenancies[0] : null;
    const currentTenancyPermissions = getCurrentTenancyPermissions(tenancyPermissions, currentTenancy) || [];

    return {
      id,
      userName: email,
      displayName: `${firstName} ${lastName}`,
      firstName,
      lastName,
      tenancies,
      tenancyPermissions,
      currentTenancy,
      currentTenancyPermissions
    };
  }

  runWithMsal(app, doNotLogin = true) {
    this.authContext
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          this.authContext.setActiveAccount(response.account);
          this.msalUser = response.account;
          app();
        }
        else {
          if (doNotLogin) {
            app();
            return;
          }
          if (this.isAuthenticated() && this.getCachedUser()) {
            app();
            return;
          }
          this.login();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  login() {
    this.authContext.loginRedirect().then((response) => {
      this.authContext.setActiveAccount(response.account);
      this.msalUser = response.account;
    });
  }

  logout() {
    this.removeCacheItem(this.CONSTANTS.ADAL_USER_DATA);
    this.authContext.logoutRedirect('/');
    this.user = null;
    this.msalUser = null;
  }

  isAuthenticated() {
    return Boolean(this.authContext.getActiveAccount());
  }

  async acquireToken() {
    const self = this;
    const account = self.msalUser || self.authContext.getActiveAccount();
    if (!account) {
      return '';
    }

    const response = await self.authContext.acquireTokenSilent({ account, scopes: ['User.Read'] });
    return response.accessToken;
  }

  async getAuthHeader(requestUrl) {
    // need help from here
    const endPoint = helpers.getApiPathName(requestUrl);

    // ------ Fetch the user tenancy details ------
    let tenancyId = null;
    let ignoreTenancy = true;

    if (!ignoreTenancyForEndpoints.includes(endPoint)) {
      const cachedUser = this.getCachedUser();
      tenancyId = cachedUser && cachedUser.currentTenancy ? cachedUser.currentTenancy.tenancyId : null;
      ignoreTenancy = false;
    }

    // ------ Fetch the token ------
    if (!ignoreTokenForEndpoints.includes(endPoint)) {
      const response = await this.authContext.acquireTokenSilent({ account: this.authContext.getActiveAccount(), scopes: ['User.Read'] });
      const token = response.accessToken;
      return {
        tenancyId,
        ignoreTenancy,
        token,
        ignoreToken: false,
        authorization: `Bearer ${token}`
      };
    }
    // ------ Token is not required. Return null ------
    return {
      tenancyId,
      ignoreTenancy,
      token: null,
      ignoreToken: true,
      authorization: null
    };
  }

  // -- FUNCTION: supportsLocalStorage
  // -- Returns true if browser supports localStorage, false otherwise.
  //---
  supportsLocalStorage = function () {
    try {
      if (!window.localStorage) return false; // Test availability
      window.localStorage.setItem('storageTest', 'A'); // Try write
      if (window.localStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.localStorage.removeItem('storageTest'); // Try delete
      if (window.localStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };

  // -- FUNCTION: supportsSessionStorage
  // -- Returns true if browser supports sessionStorage, false otherwise.
  //---
  supportsSessionStorage = function () {
    try {
      if (!window.sessionStorage) return false; // Test availability
      window.sessionStorage.setItem('storageTest', 'A'); // Try write
      if (window.sessionStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.sessionStorage.removeItem('storageTest'); // Try delete
      if (window.sessionStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };

  // -- FUNCTION: getCacheItem
  // -- Searches the value for the given key in the cache
  //---
  getCacheItem = function (key) {
    const authContext = this.authContext || {};
    const config = authContext.config || {};
    const { cacheLocation } = config;

    // Use localStorage
    if (cacheLocation === 'localStorage') {
      if (!this.supportsLocalStorage()) {
        console.log('Local storage is not supported');
        return null;
      }
      return localStorage.getItem(key);
    }

    // Default as sessionStorage
    if (!this.supportsSessionStorage()) {
      console.log('Session storage is not supported');
      return null;
    }
    return sessionStorage.getItem(key);
  };

  setCacheItem(key, obj) {
    const authContext = this.authContext || {};
    const config = authContext.config || {};
    const { cacheLocation } = config;

    // Use localStorage
    if (cacheLocation === 'localStorage') {
      if (!this.supportsLocalStorage()) {
        return false;
      }
      localStorage.setItem(key, obj);
      return true;
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }
    sessionStorage.setItem(key, obj);
    return true;
  }

  // -- FUNCTION: removeCacheItem
  // -- Saves the key-value pair in the cache
  // ---
  removeCacheItem(key) {
    const authContext = this.authContext || {};
    const config = authContext.config || {};
    const { cacheLocation } = config;

    // Use localStorage
    if (cacheLocation === 'localStorage') {
      if (!this.supportsLocalStorage()) {
        return false;
      }
      localStorage.removeItem(key);
      return true;
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }

    sessionStorage.removeItem(key);
    return true;
  }

  getCachedUser() {
    if (this.user) {
      return this.user;
    }

    // -- User data is empty. Check the cache to see if we can fetch
    // -- the user data from there. If the cache data is empty, logout
    // -- and redirect user to login. We need the user data to correctly
    // -- setup the user's tenancy access and permissions.
    // ---
    const userData = JSON.parse(this.getCacheItem(this.CONSTANTS.CR_USER_DATA));

    if (userData) {
      this.user = userData;
    }

    const account = this.authContext.getActiveAccount();
    if (account) {
      this.msalUser = account;
    }
    return this.user;
  }

  getUser() {
    // ------ Fetch the user's details ------
    return axios.get(ApiEndpoints.whoami).then(
      (response) => {
        const userData = response.data.toJS() || {};
        this.user = MsalService.createUser(userData);
        this.setCacheItem(this.CONSTANTS.CR_USER_DATA, JSON.stringify(this.user));

        return this.user;
      },
      (error) => {
        this.user = null;
        this.msalUser = null;
        this.removeCacheItem(this.CONSTANTS.CR_USER_DATA);
        throw error;
      }
    );
  }

  setCurrentTenancy(tenancy) {
    const cachedUser = this.getCachedUser();
    if (cachedUser) {
      cachedUser.currentTenancy = tenancy;
      cachedUser.currentTenancyPermissions = getCurrentTenancyPermissions(cachedUser.tenancyPermissions, tenancy) || [];
      this.setCacheItem(this.CONSTANTS.CR_USER_DATA, JSON.stringify(cachedUser));
    }
  }
}

export default new MsalService(settings.MSAL_CONFIG);
